import { useTranslation } from 'react-i18next';
import type { UsePaymentProvidersResult } from '@/api/payments';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { cn } from '@/lib/cn';
import { EmptyState, Icon, Input, Spinner, Toast } from '@/ui/common';
import { ProviderRow } from './ProviderRow';

export interface ProviderListPanelProps {
  providers: UsePaymentProvidersResult;
  onSelect: (providerId: number) => void;
  selectedProviderId?: number | null;
  className?: string;
}

export function ProviderListPanel({
  providers,
  onSelect,
  selectedProviderId = null,
  className,
}: ProviderListPanelProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const { filtered, search, setSearch, isLoading, error, currency } = providers;
  const count = filtered.length;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="relative min-w-0 flex-1">
          <Icon
            name="search"
            size={16}
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-text-secondary"
          />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={label('Search providers', 'Search providers')}
            aria-label={label('Search providers', 'Search providers')}
            className="[&_input]:pl-9"
          />
        </div>
        <p className="shrink-0 text-[12px] text-text-secondary sm:text-right">
          {label('{count} providers', `${count} providers`).replace(
            '{count}',
            String(count),
          )}
        </p>
      </div>

      {error ? <Toast tone="danger">{error.message}</Toast> : null}

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner size="md" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="deposit"
          title={label('No providers', 'No providers')}
          description={label(
            'No payment providers match your search.',
            'No payment providers match your search.',
          )}
        />
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.map((provider) => (
            <ProviderRow
              key={provider.Id}
              provider={provider}
              currency={currency}
              selected={selectedProviderId === provider.Id}
              redepositLabel={label('Redeposit', 'Redeposit')}
              onClick={() => onSelect(provider.Id)}
            />
          ))}
          {count > 8 ? (
            <p className="pt-1 text-center text-[12px] text-text-secondary">
              {label('Scroll for more providers…', 'Scroll for more providers…')}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
